import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Label } from "@shared/ui/label";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { RadioGroup, RadioGroupItem } from "@shared/ui/radio-group";
import { MapPin, Eye, GripVertical, X, Edit } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  useCreateNscAddressComponentConfiguration,
  useGetAllAddressComponent,
  useGetNscResponseTimeConfiguration,
  useUpdateNscAddressComponentConfiguration,
} from "@features/cx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface SelectedComponent {
  key: string;
  order: number;
}

interface AddressComponent {
  key: string;
  label: string;
  description: string;
}

export interface AddressConfigData {
  selectedComponents: SelectedComponent[];
  displayFormat: string;
}

export interface AddressConfigurationCardProps {}

export interface AddressConfigurationCardRef {
  getData: () => AddressConfigData;
}

type SampleAddressKey =
  | "unit"
  | "premise"
  | "region"
  | "country"
  | "state"
  | "county"
  | "area"
  | "subarea"
  | "division"
  | "zone"
  | "zipcode";

interface SampleAddress {
  unit: string;
  premise: string;
  region: string;
  country: string;
  state: string;
  county: string;
  area: string;
  subarea: string;
  division: string;
  zone: string;
  zipcode: string;
}

export const AddressConfigurationCard = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: addressComponents } = useGetAllAddressComponent();
  const { data: addressComponentConfiguration } =
    useGetNscResponseTimeConfiguration();
  const { mutateAsync: updateAddressComponentConfiguration } =
    useUpdateNscAddressComponentConfiguration();
  const { mutateAsync: createAddressComponentConfiguration } =
    useCreateNscAddressComponentConfiguration();

  // Find the address display configuration using config_type
  const addressDisplayConfig = addressComponentConfiguration?.find(
    (config) => config.configType === "ADDRESS_DISPLAY"
  );

  const [isEditable, setIsEditable] = useState(
    !addressDisplayConfig?.configurationData // editable by default if no config
  );

  const [selectedAddressComponents, setSelectedAddressComponents] = useState<
    SelectedComponent[]
  >([]);

  const [addressDisplayFormat, setAddressDisplayFormat] =
    useState<string>("single");

  const sampleAddress: SampleAddress = {
    unit: "Unit 205",
    premise: "Sunrise Apartments",
    region: "North Central",
    country: "Sri Lanka",
    state: "Western Province",
    county: "Colombo",
    area: "Pettah",
    subarea: "Commercial District",
    division: "Colombo Central",
    zone: "Zone 1",
    zipcode: "10001",
  };

  // Transform API data to match component structure
  const transformedAddressComponents: AddressComponent[] =
    addressComponents?.map((component) => {
      let key = component.value.toLowerCase().replace(/\s+/g, "");
      // Handle special cases for key mapping
      if (key === "subarea") key = "subarea";
      if (key === "zipcode") key = "zipcode";

      return {
        key,
        label: component.value,
        description: component.description,
      };
    }) || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load existing configuration data
  useEffect(() => {
    if (addressDisplayConfig?.configurationData) {
      const config = addressDisplayConfig.configurationData;
      const configuredComponents: SelectedComponent[] = Object.entries(config)
        .map(([key, order]) => ({
          key: key.toLowerCase(),
          order: Number(order),
        }))
        .sort((a, b) => a.order - b.order);

      setSelectedAddressComponents(configuredComponents);
      setIsEditable(false); // disable fields once data is fetched
    } else {
      setIsEditable(true); // editable if no data
    }
  }, [addressDisplayConfig]);

  const handleAddAddressComponent = (componentKey: string): void => {
    if (
      !selectedAddressComponents?.find(
        (comp: SelectedComponent) => comp.key === componentKey
      )
    ) {
      const newOrder = selectedAddressComponents.length + 1;
      setSelectedAddressComponents((prev) => [
        ...prev,
        { key: componentKey, order: newOrder },
      ]);
    }
  };

  const handleRemoveAddressComponent = (componentKey: string): void => {
    setSelectedAddressComponents((prev) => {
      const filtered = prev.filter(
        (comp: SelectedComponent) => comp.key !== componentKey
      );
      return filtered.map((comp, index) => ({ ...comp, order: index + 1 }));
    });
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSelectedAddressComponents((items: SelectedComponent[]) => {
        const oldIndex = items.findIndex(
          (item: SelectedComponent) => item.key === active.id
        );
        const newIndex = items.findIndex(
          (item: SelectedComponent) => item.key === over?.id
        );

        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((item, index) => ({ ...item, order: index + 1 }));
      });
    }
  };

  const handleSaveChanges = (): void => {
    const operation = addressDisplayConfig?.configurationData
      ? "UPDATE"
      : "ADD";

    // Transform selectedAddressComponents to the required payload format
    const configurationData: Record<string, number> = {};
    selectedAddressComponents.forEach((comp) => {
      configurationData[comp.key.toUpperCase()] = comp.order;
    });

    const payload = {
      remote_utility_id: remoteUtilityId,
      config_type: "ADDRESS_DISPLAY",
      configuration_data: configurationData,
    };

    switch (operation) {
      case "UPDATE":
        updateAddressComponentConfiguration(payload);
        break;
      case "ADD":
        createAddressComponentConfiguration(payload);
        break;
    }

    setIsEditable(false);
  };

  const handleReset = (): void => {
    if (addressDisplayConfig?.configurationData) {
      const config = addressDisplayConfig.configurationData;
      const configuredComponents: SelectedComponent[] = Object.entries(config)
        .map(([key, order]) => ({
          key: key.toLowerCase(),
          order: Number(order),
        }))
        .sort((a, b) => a.order - b.order);

      setSelectedAddressComponents(configuredComponents);
    } else {
      setSelectedAddressComponents([]);
    }
  };

  const generateAddressPreview = (): string => {
    const sortedComponents = selectedAddressComponents
      .sort((a: SelectedComponent, b: SelectedComponent) => a.order - b.order)
      .map(
        (comp: SelectedComponent) => sampleAddress[comp.key as SampleAddressKey]
      )
      .filter(Boolean);

    if (sortedComponents.length === 0) return "No components selected";

    switch (addressDisplayFormat) {
      case "single":
        return sortedComponents.join(", ");
      default:
        return sortedComponents.join(", ");
    }
  };

  const availableAddressComponents = transformedAddressComponents?.filter(
    (comp: AddressComponent) =>
      !selectedAddressComponents?.find(
        (selected: SelectedComponent) => selected.key === comp.key
      )
  );

  // Inline SortableAddressComponent function
  const SortableAddressItem = ({ comp }: { comp: SelectedComponent }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: comp.key });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const componentInfo = transformedAddressComponents?.find(
      (c: AddressComponent) => c.key === comp.key
    );

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center justify-between p-3 border rounded-lg bg-background"
      >
        <div className="flex items-center gap-3 flex-1">
          <div
            {...attributes}
            {...listeners}
            className={`${
              isEditable
                ? "cursor-grab active:cursor-grabbing"
                : "cursor-not-allowed opacity-50"
            }`}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <Label className="text-sm font-medium">
              {componentInfo?.label}
            </Label>
            <p className="text-xs text-muted-foreground">
              {componentInfo?.description}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-xs text-muted-foreground">
            Order: {comp.order}
          </span>
          {isEditable && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleRemoveAddressComponent(comp.key)}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="card-heading-text flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Address Display Configuration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <p className="card-subheading-text ml-7">
              Configure how addresses are displayed throughout the platform by
              selecting components and setting their display order through drag
              and drop.
            </p>
            {!isEditable && (
              <div className="flex justify-end">
                <button
                  onClick={() => setIsEditable(true)}
                  className="border border-input text-sm px-3 py-1.5 bg-white text-black rounded hover:bg-gray-50 flex items-center gap-1"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium text-sm">Address Components</h4>

              {isEditable && (
                <div className="flex gap-2">
                  <Select onValueChange={handleAddAddressComponent}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Add address component..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableAddressComponents?.map(
                        (component: AddressComponent) => (
                          <SelectItem key={component.key} value={component.key}>
                            {component.label}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={isEditable ? handleDragEnd : undefined}
                >
                  <SortableContext
                    items={selectedAddressComponents?.map((comp) => comp.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {selectedAddressComponents
                      ?.sort(
                        (a: SelectedComponent, b: SelectedComponent) =>
                          a.order - b.order
                      )
                      ?.map((comp: SelectedComponent) => (
                        <SortableAddressItem key={comp.key} comp={comp} />
                      ))}
                  </SortableContext>
                </DndContext>

                {selectedAddressComponents?.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {isEditable
                      ? "No address components selected. Use the dropdown above to add components."
                      : "No address components configured."}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-sm">Address Preview</h4>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </div>

              <div className="border rounded-lg p-4 bg-muted/50">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">
                      Sample Address Display:
                    </Label>
                    <div className="text-sm mt-1 p-2 bg-background border rounded whitespace-pre-line">
                      {generateAddressPreview()}
                    </div>
                  </div>

                  <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded border-l-4 border-blue-600">
                    <p className="font-medium">
                      ℹ️ Address Display Information
                    </p>
                    <p className="mt-1">
                      This address format will be used consistently across the
                      entire platform, including on bills, invoices, service
                      requests, customer profiles, and all other documents and
                      interfaces.
                    </p>
                  </div>

                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>
                      <strong>Selected Components:</strong>
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {selectedAddressComponents
                        ?.sort(
                          (a: SelectedComponent, b: SelectedComponent) =>
                            a.order - b.order
                        )
                        ?.map((comp: SelectedComponent) => {
                          const componentInfo =
                            transformedAddressComponents?.find(
                              (c: AddressComponent) => c.key === comp.key
                            );
                          return (
                            <span
                              key={comp.key}
                              className="bg-primary/10 text-primary px-2 py-1 rounded text-xs"
                            >
                              {componentInfo?.label} ({comp.order})
                            </span>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isEditable && (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleReset} className="text-sm">
              Cancel
            </Button>
            <Button onClick={handleSaveChanges} className="text-sm">
              Save
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
